/* ============================================================================
   Ask Zana conversation persistence (browser-local).

   Active thread → sessionStorage (survives tab switches inside the SPA;
   clears when the browser tab/site is closed).
   History → localStorage (past threads, keyed by org + uid).

   Clears / archives when:
   • owner taps New conversation
   • ~1h idle with the site open (or on next open after 1h)
   • logout (active cleared; history stays for that uid)
   • tab/site closed (sessionStorage gone — active only)
   ========================================================================== */

import type { ZanaIntent } from './zanaActions';
import { bridgeZanaThread } from './briefingsApi';
import type { ZanaEpisode } from './zanaEpisodes';

export interface ZanaChatMsg {
  from: 'you' | 'zana';
  text: string;
  /** Set on answers that can hold a write, so a typed "yes" can prove it is
   *  answering THAT message (see HeldConfirm in zanaAskShared). */
  id?: string;
  /** 2026-09-17: the org an answer was asked in. Its chips run only while that
   *  org is open (chipMayRun in zanaAskShared). */
  orgCode?: string;
  intents?: ZanaIntent[];
  /** Explain vs take-over dual offer — same on dock and full-screen. */
  offer?: {
    topic: string;
    kind: 'do' | 'show';
    label: string;
    explainLabel?: string;
    prompt: string;
  };
  /** When this answer came back while Zana was resting, the meter state that
   *  drives the "resting · back around 4:20pm · more credits" footer. */
  restingLimit?: {
    state: 'resting';
    /* `month` (the allowance is spent until the 1st) and `accounting` (a fault
       on our side, not a budget) carry NO resetAtMs — there is no honest clock
       to quote for either, which is why it is optional. Saying "back tomorrow"
       for a month that resets in four weeks is how this went wrong before. */
    reason: 'window' | 'daily' | 'month' | 'accounting';
    scope: 'org' | 'user' | null;
    used?: number;
    max?: number;
    resetAtMs?: number;
    upgrade?: boolean;
  };
  /** ai | deterministic | cache | static — surfaced as a subtle badge in chat. */
  source?: 'ai' | 'deterministic' | 'cache' | 'static' | 'local';
  /** Structured memory of a walk / FG / BG agent run — Ask payload v2. */
  episode?: ZanaEpisode;
}

export interface ZanaConversation {
  id: string;
  orgCode: string;
  uid: string;
  title: string;
  messages: ZanaChatMsg[];
  createdAt: number;
  updatedAt: number;
}

const IDLE_MS = 60 * 60 * 1000; // 1 hour
const MAX_HISTORY = 25;
const MAX_MESSAGES = 120;

function activeKey(orgCode: string, uid: string) {
  return `zana_ask_active_v1:${orgCode}:${uid}`;
}
function historyKey(orgCode: string, uid: string) {
  return `zana_ask_history_v1:${orgCode}:${uid}`;
}

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function titleFromMessages(messages: ZanaChatMsg[]): string {
  const first = messages.find((m) => m.from === 'you' && m.text.trim());
  const t = (first?.text || 'Conversation').trim().replace(/\s+/g, ' ');
  return t.length > 64 ? `${t.slice(0, 61)}…` : t;
}

function newId(): string {
  return `z${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function trimMessages(messages: ZanaChatMsg[]): ZanaChatMsg[] {
  if (messages.length <= MAX_MESSAGES) return messages;
  return messages.slice(-MAX_MESSAGES);
}

/* The active thread lived in sessionStorage, which is per browser TAB and dies
   on any hard navigation or reload — and the dock, unlike the full-screen page,
   registered no `pagehide` archive, so a conversation held only in the dock was
   destroyed with no History entry to recover it. That is the "dock truncates
   history after navigation" symptom.

   localStorage instead, keyed by org and uid exactly as before. The 1-hour idle
   archive still runs, and `clearActiveOnLogout` still wipes it, so nothing about
   the lifecycle changes — only its survival across a page load. sessionStorage
   is still READ once, so a thread open across the deploy migrates rather than
   vanishing. */
function activeStore(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

function readActiveRaw(orgCode: string, uid: string): ZanaConversation | null {
  if (!orgCode || !uid) return null;
  const store = activeStore();
  const key = activeKey(orgCode, uid);
  let conv = safeParse<ZanaConversation>(store ? store.getItem(key) : null);
  if (!conv && typeof sessionStorage !== 'undefined') {
    // One-time migration from the old per-tab home.
    conv = safeParse<ZanaConversation>(sessionStorage.getItem(key));
  }
  if (!conv || !Array.isArray(conv.messages) || conv.orgCode !== orgCode || conv.uid !== uid) {
    return null;
  }
  return conv;
}

/** Load the active conversation, archiving it if idle past 1h. */
export function loadActiveConversation(orgCode: string, uid: string): ZanaConversation | null {
  const conv = readActiveRaw(orgCode, uid);
  if (!conv) return null;
  if (Date.now() - (conv.updatedAt || 0) > IDLE_MS) {
    if (conv.messages.length > 0) archiveConversation(conv);
    clearActiveConversation(orgCode, uid);
    return null;
  }
  return conv;
}

export function saveActiveConversation(
  orgCode: string,
  uid: string,
  messages: ZanaChatMsg[],
  existingId?: string | null,
  opts: { allowClear?: boolean } = {},
): ZanaConversation | null {
  if (!orgCode || !uid) return null;
  if (!messages.length) {
    /* An empty in-memory render is NOT a command to delete the stored thread.
       A dock that mounts before its hydration effect runs renders zero
       messages for one frame, and that frame used to wipe the conversation —
       indistinguishable, at this layer, from the user pressing New. Only an
       explicit caller (New conversation / logout) may clear. */
    if (opts.allowClear) clearActiveConversation(orgCode, uid);
    return null;
  }
  const prev = readActiveRaw(orgCode, uid);
  const now = Date.now();
  const conv: ZanaConversation = {
    id: existingId || prev?.id || newId(),
    orgCode,
    uid,
    title: titleFromMessages(messages),
    messages: trimMessages(messages),
    createdAt: prev?.createdAt || now,
    updatedAt: now,
  };
  try {
    const store = activeStore();
    if (store) store.setItem(activeKey(orgCode, uid), JSON.stringify(conv));
    notifyZanaChatListeners();
  } catch {
    /* quota / private mode — chat still works in memory */
  }
  return conv;
}

export function clearActiveConversation(orgCode: string, uid: string): void {
  if (!orgCode || !uid) return;
  try {
    const store = activeStore();
    if (store) store.removeItem(activeKey(orgCode, uid));
    // Clear the legacy per-tab copy too, so a migrated thread cannot come back.
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(activeKey(orgCode, uid));
    }
    notifyZanaChatListeners();
  } catch { /* ignore */ }
}

/** Append a Zana line into the active thread (takeover summary, refusal, etc.).
 *  Both the dock and the full-screen tab subscribe, so the line shows in both.
 *  Optional `episode` attaches structured agent memory for Ask history v2. */
export function appendZanaNotice(
  orgCode: string,
  uid: string,
  text: string,
  episode?: ZanaEpisode | null,
): void {
  const t = String(text || '').trim();
  if (!orgCode || !uid || !t) return;
  const active = loadActiveConversation(orgCode, uid);
  const msg: ZanaChatMsg = { from: 'zana', text: t };
  if (episode && episode.type === 'agent_episode') msg.episode = episode;
  const messages = [...(active?.messages || []), msg];
  saveActiveConversation(orgCode, uid, messages, active?.id || null);
}

type ZanaChatListener = () => void;
const zanaChatListeners = new Set<ZanaChatListener>();

function notifyZanaChatListeners(): void {
  zanaChatListeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

/** Same-tab sync between dock and Ask Zana full page. */
export function subscribeZanaChat(fn: ZanaChatListener): () => void {
  zanaChatListeners.add(fn);
  return () => { zanaChatListeners.delete(fn); };
}

export function listConversationHistory(orgCode: string, uid: string): ZanaConversation[] {
  if (!orgCode || !uid || typeof localStorage === 'undefined') return [];
  const list = safeParse<ZanaConversation[]>(localStorage.getItem(historyKey(orgCode, uid)));
  if (!Array.isArray(list)) return [];
  return list
    .filter((c) => c && c.orgCode === orgCode && c.uid === uid && Array.isArray(c.messages))
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export function archiveConversation(conv: ZanaConversation): void {
  if (!conv?.orgCode || !conv.uid || !conv.messages?.length) return;
  if (typeof localStorage === 'undefined') return;
  const key = historyKey(conv.orgCode, conv.uid);
  const prev = listConversationHistory(conv.orgCode, conv.uid).filter((c) => c.id !== conv.id);
  const next = [
    {
      ...conv,
      title: titleFromMessages(conv.messages),
      messages: trimMessages(conv.messages),
      updatedAt: Date.now(),
    },
    ...prev,
  ].slice(0, MAX_HISTORY);
  try {
    localStorage.setItem(key, JSON.stringify(next));
    /* Fold a one-line summary into org memory so a later ask can relate to
       this thread — best-effort, never blocks the archive. */
    void bridgeZanaThread(conv.orgCode, conv.messages);
  } catch { /* ignore */ }
}

/**
 * Archive whatever is active WITHOUT clearing it — for tab close.
 *
 * The active thread lives in sessionStorage, which dies with the tab, so before
 * this a conversation only ever reached History if the owner tapped New
 * conversation, went idle an hour, or restored another one. Close the tab
 * mid-conversation and it was gone silently. Archiving is idempotent (it
 * replaces any entry with the same id), so doing it on the way out and again
 * later cannot produce a duplicate.
 */
export function archiveActiveConversation(orgCode: string, uid: string): void {
  const conv = readActiveRaw(orgCode, uid);
  if (conv && conv.messages.length) archiveConversation(conv);
}

/** Archive current active (if any), then clear it — used by New conversation. */
export function startNewConversation(orgCode: string, uid: string): void {
  const cur = loadActiveConversation(orgCode, uid);
  if (cur && cur.messages.length) archiveConversation(cur);
  clearActiveConversation(orgCode, uid);
}

export function getHistoryConversation(
  orgCode: string,
  uid: string,
  id: string,
): ZanaConversation | null {
  return listConversationHistory(orgCode, uid).find((c) => c.id === id) || null;
}

/** Restore a history item into the active session slot. */
export function restoreConversation(orgCode: string, uid: string, id: string): ZanaConversation | null {
  const found = getHistoryConversation(orgCode, uid, id);
  if (!found) return null;
  // Archive whatever was in progress first.
  const cur = loadActiveConversation(orgCode, uid);
  if (cur && cur.messages.length && cur.id !== found.id) archiveConversation(cur);
  const restored: ZanaConversation = {
    ...found,
    updatedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(activeKey(orgCode, uid), JSON.stringify(restored));
    notifyZanaChatListeners();
  } catch { /* ignore */ }
  return restored;
}

export function deleteHistoryConversation(orgCode: string, uid: string, id: string): void {
  if (typeof localStorage === 'undefined') return;
  const next = listConversationHistory(orgCode, uid).filter((c) => c.id !== id);
  try {
    localStorage.setItem(historyKey(orgCode, uid), JSON.stringify(next));
  } catch { /* ignore */ }
}

/**
 * Drop active thread for this user across orgs (logout). History kept.
 *
 * BOTH stores. The active thread moved to localStorage so it survives a page
 * navigation, and this function only swept sessionStorage — so logging out
 * would have LEFT the conversation on the device, which is precisely what
 * clearing on logout exists to prevent. On a shared office machine that is a
 * fleet's alert counts and plates sitting in storage after the owner has gone.
 */
export function clearActiveOnLogout(uid: string | null | undefined): void {
  if (!uid) return;
  const prefix = 'zana_ask_active_v1:';
  const sweep = (store: Storage | null) => {
    if (!store) return;
    try {
      const keys: string[] = [];
      for (let i = 0; i < store.length; i += 1) {
        const k = store.key(i);
        if (k && k.startsWith(prefix) && k.endsWith(`:${uid}`)) keys.push(k);
      }
      keys.forEach((k) => store.removeItem(k));
    } catch { /* ignore */ }
  };
  sweep(activeStore());
  try {
    sweep(typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  } catch { /* ignore */ }
}

export const ZANA_CHAT_IDLE_MS = IDLE_MS;
